// frontend/src/pages/LandingPage/RecursosPage.jsx
// Página de Recursos e Aplicações do Higeia.tech
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    LayoutDashboard,
    Users,
    Calendar,
    CalendarCheck,
    Pill,
    Monitor,
    FileText,
    Clock,
    UserCog,
    Shield,
    FileSearch,
    CreditCard,
    BarChart3,
    CheckCircle2,
    Zap,
    Target,
    Heart,
    ArrowRight,
    Sparkles,
    Brain,
    Bot,
    Clock4
} from 'lucide-react';
import HeaderPublic from '../../components/Navigation/HeaderPublic';
import Footer from '../../components/LandingPage/Footer';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { Link } from 'react-router-dom';

const RecursosPage = () => {
    // Garante que a página sempre comece no topo ao carregar
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, []);

    // Categorias de recursos
    const categoriasRecursos = [
        {
            id: 'gestao-pacientes',
            title: 'Gestão de Pacientes',
            description: 'Soluções completas para gerenciar informações e histórico de pacientes',
            icon: Users,
            color: 'blue',
            recursos: [
                {
                    title: 'Prontuários Eletrônicos',
                    description: 'Sistema completo de prontuários digitais com histórico médico completo, evoluções e anamnese. Armazene de forma segura e acesse rapidamente todas as informações do paciente.',
                    icon: FileSearch,
                    features: ['Histórico médico completo', 'Evoluções e anamnese', 'Exames e resultados', 'Fotos e documentos']
                },
                {
                    title: 'Gestão de Pacientes',
                    description: 'Cadastro completo de pacientes com informações pessoais, planos de saúde, contatos de emergência e muito mais. Controle total sobre a base de pacientes.',
                    icon: Users,
                    features: ['Cadastro completo', 'Planos de saúde', 'Contatos de emergência', 'Histórico de atendimentos']
                },
                {
                    title: 'Planos Médicos',
                    description: 'Gerencie informações de planos de saúde dos pacientes, incluindo coberturas, autorizações e histórico de utilização.',
                    icon: CreditCard,
                    features: ['Cadastro de planos', 'Coberturas e limites', 'Autorizações', 'Histórico de uso']
                }
            ]
        },
        {
            id: 'agenda-operacoes',
            title: 'Agenda e Operações',
            description: 'Ferramentas para otimizar agendamentos, tarefas e rotinas de cuidado',
            icon: Calendar,
            color: 'green',
            recursos: [
                {
                    title: 'Agendamentos',
                    description: 'Sistema completo de agendamento de consultas e atendimentos. Organize a agenda de profissionais, gerencie horários e evite conflitos de agendamento.',
                    icon: Calendar,
                    features: ['Agenda por profissional', 'Confirmação de horários', 'Lembretes automáticos', 'Histórico de agendamentos']
                },
                {
                    title: 'Tarefas Agendadas',
                    description: 'Controle completo de tarefas e cuidados agendados. Crie tarefas recorrentes, atribua responsáveis e acompanhe o status de cada cuidado prestado.',
                    icon: CalendarCheck,
                    features: ['Tarefas recorrentes', 'Atribuição de responsáveis', 'Status em tempo real', 'Alertas de vencimento']
                },
                {
                    title: 'Diário de Cuidados',
                    description: 'Registre diariamente todos os cuidados prestados aos pacientes. Mantenha um histórico detalhado de evoluções, medicações administradas e observações.',
                    icon: FileText,
                    features: ['Registros diários', 'Observações e evoluções', 'Histórico completo', 'Busca avançada']
                }
            ]
        },
        {
            id: 'controle-estoque',
            title: 'Controle de Estoque e Equipamentos',
            description: 'Gerencie medicamentos, materiais e equipamentos com inteligência',
            icon: Pill,
            color: 'purple',
            recursos: [
                {
                    title: 'Gestão de Medicamentos',
                    description: 'Controle completo de estoque de medicamentos com alertas de validade, reposição automática e histórico de consumo. Evite desperdícios e garanta disponibilidade.',
                    icon: Pill,
                    features: ['Controle de estoque', 'Alertas de validade', 'Histórico de consumo', 'Reposição automática']
                },
                {
                    title: 'Plano de Medicações',
                    description: 'Crie e gerencie planos de medicação personalizados para cada paciente. Configure horários, dosagens, frequência e receba alertas para administração. Garanta a adesão ao tratamento com controle completo.',
                    icon: CalendarCheck,
                    features: ['Planos personalizados por paciente', 'Alertas de administração', 'Controle de dosagem e horários', 'Histórico de administrações']
                },
                {
                    title: 'Monitoramento de Equipamentos',
                    description: 'Gerencie equipamentos médicos com controle de localização, status de manutenção e disponibilidade. Mantenha todos os equipamentos organizados e funcionais.',
                    icon: Monitor,
                    features: ['Localização de equipamentos', 'Status de manutenção', 'Histórico de uso', 'Calibração e certificações']
                }
            ]
        },
        {
            id: 'pessoas-presenca',
            title: 'Pessoas e Presença',
            description: 'Gestão de equipe, controle de ponto e permissões',
            icon: UserCog,
            color: 'orange',
            recursos: [
                {
                    title: 'Check In/Out',
                    description: 'Sistema de controle de ponto para profissionais. Registre entradas e saídas, acompanhe horas trabalhadas e garanta a presença adequada da equipe.',
                    icon: Clock,
                    features: ['Registro de ponto', 'Histórico de presença', 'Relatórios de horas', 'Alertas de atraso'],
                    cta: { label: 'Ver planos de ponto', href: '/planos/registro-de-ponto' }
                },
                {
                    title: 'Gestão de Usuários',
                    description: 'Gerencie usuários do sistema com diferentes perfis e responsabilidades. Vincule usuários à organização e defina níveis de acesso.',
                    icon: UserCog,
                    features: ['Cadastro de usuários', 'Perfis e funções', 'Vinculação à organização', 'Ativação e desativação']
                },
                {
                    title: 'Sistema de Permissões (RBAC)',
                    description: 'Controle granular de permissões com Role-Based Access Control. Defina exatamente o que cada perfil de usuário pode acessar e modificar.',
                    icon: Shield,
                    features: ['Controle granular de acesso', 'Perfis personalizáveis', 'Permissões por recurso', 'Auditoria de ações']
                }
            ]
        },
        {
            id: 'analytics-visao',
            title: 'Analytics e Visão Geral',
            description: 'Dashboards e relatórios para tomada de decisão baseada em dados',
            icon: BarChart3,
            color: 'teal',
            recursos: [
                {
                    title: 'Dashboard Executivo',
                    description: 'Visão consolidada de KPIs e métricas importantes do negócio. Acompanhe indicadores de desempenho, eficiência e conformidade em tempo real.',
                    icon: LayoutDashboard,
                    features: ['KPIs principais', 'Métricas de eficiência', 'Alertas importantes', 'Visão consolidada']
                },
                {
                    title: 'Relatórios e Análises',
                    description: 'Gere relatórios detalhados sobre pacientes, atendimentos, estoque e muito mais. Exporte dados para análises externas e tomadas de decisão.',
                    icon: BarChart3,
                    features: ['Relatórios personalizáveis', 'Gráficos e visualizações', 'Exportação de dados', 'Análises temporais']
                }
            ]
        }
    ];

    // Recursos adicionais (benefícios)
    const beneficios = [
        {
            icon: Shield,
            title: 'Segurança e Conformidade',
            description: 'Todos os dados protegidos com criptografia SSL e conformidade total com LGPD.',
            color: 'emerald'
        },
        {
            icon: Zap,
            title: 'Eficiência Operacional',
            description: 'Reduza o tempo gasto em tarefas administrativas em até 40% com automações inteligentes.',
            color: 'yellow'
        },
        {
            icon: Target,
            title: 'Precisão e Confiabilidade',
            description: 'Elimine erros manuais com validações automáticas e alertas em tempo real.',
            color: 'blue'
        },
        {
            icon: Heart,
            title: 'Foco no Cuidado',
            description: 'Concentre-se no que realmente importa: o bem-estar e cuidado dos pacientes.',
            color: 'pink'
        }
    ];

    const colorClasses = {
        blue: {
            bg: 'bg-blue-50',
            border: 'border-blue-200',
            icon: 'text-blue-600',
            title: 'text-blue-900',
            gradient: 'from-blue-600 to-blue-800'
        },
        green: {
            bg: 'bg-green-50',
            border: 'border-green-200',
            icon: 'text-green-600',
            title: 'text-green-900',
            gradient: 'from-green-600 to-green-800'
        },
        purple: {
            bg: 'bg-purple-50',
            border: 'border-purple-200',
            icon: 'text-purple-600',
            title: 'text-purple-900',
            gradient: 'from-purple-600 to-purple-800'
        },
        orange: {
            bg: 'bg-orange-50',
            border: 'border-orange-200',
            icon: 'text-orange-600',
            title: 'text-orange-900',
            gradient: 'from-orange-600 to-orange-800'
        },
        teal: {
            bg: 'bg-teal-50',
            border: 'border-teal-200',
            icon: 'text-teal-600',
            title: 'text-teal-900',
            gradient: 'from-teal-600 to-teal-800'
        },
        emerald: {
            bg: 'bg-emerald-50',
            icon: 'text-emerald-600'
        },
        yellow: {
            bg: 'bg-yellow-50',
            icon: 'text-yellow-600'
        },
        pink: {
            bg: 'bg-pink-50',
            icon: 'text-pink-600'
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header Público */}
            <div className="bg-white shadow-sm">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <HeaderPublic />
                </div>
            </div>

            {/* Conteúdo Principal */}
            <main>
                {/* Hero Section */}
                <motion.section
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="relative py-20 sm:py-24 bg-gradient-to-br from-higeia-dark via-higeia-medium to-higeia-dark text-white overflow-hidden"
                >
                    {/* Padrão decorativo */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0" style={{
                            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                            backgroundSize: '40px 40px'
                        }} />
                    </div>

                    <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6"
                        >
                            <LayoutDashboard className="h-10 w-10" />
                        </motion.div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
                            Recursos e{' '}
                            <span className="bg-gradient-to-r from-white to-higeia-light bg-clip-text text-transparent">
                                Aplicações
                            </span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
                            Descubra todas as funcionalidades poderosas do Higeia.tech para transformar a gestão de cuidados de saúde
                        </p>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4, duration: 0.6 }}
                        >
                            <Link to="/demonstracao">
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <PrimaryButton className="!px-8 !py-4 !text-lg !font-bold !bg-white !text-higeia-dark hover:!bg-gray-100 !shadow-xl !border-0">
                                        Experimente Grátis
                                        <ArrowRight className="h-5 w-5 ml-2" />
                                    </PrimaryButton>
                                </motion.div>
                            </Link>
                        </motion.div>
                    </div>
                </motion.section>

                {/* Seção: IA Higeia em Destaque */}
                <section className="py-16 sm:py-20 bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="max-w-5xl mx-auto"
                        >
                            <div className="bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 rounded-3xl p-8 sm:p-12 shadow-2xl text-white relative overflow-hidden">
                                {/* Padrão decorativo */}
                                <div className="absolute inset-0 opacity-10">
                                    <div className="absolute inset-0" style={{
                                        backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                                        backgroundSize: '40px 40px'
                                    }} />
                                </div>
                                
                                <div className="relative z-10">
                                    {/* Badge */}
                                    <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                                        <Sparkles className="h-5 w-5" />
                                        <span className="font-bold text-sm">DESTAQUE</span>
                                    </div>

                                    {/* Cabeçalho */}
                                    <div className="flex flex-col md:flex-row items-start md:items-center gap-6 mb-8">
                                        <div className="flex-shrink-0 w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                                            <Brain className="h-10 w-10" />
                                        </div>
                                        <div className="flex-1">
                                            <h2 className="text-3xl sm:text-4xl font-black mb-3">
                                                IA Higeia
                                            </h2>
                                            <p className="text-xl text-white/90 leading-relaxed">
                                                Inteligência Artificial que auxilia na automação e otimização de processos
                                            </p>
                                        </div>
                                    </div>

                                    {/* Descrição */}
                                    <p className="text-lg text-white/95 leading-relaxed mb-8 max-w-3xl">
                                        A IA Higeia é nossa assistente inteligente integrada ao sistema, desenvolvida para automatizar tarefas repetitivas, sugerir otimizações e auxiliar na tomada de decisões baseadas em dados. Transforme a eficiência operacional com o poder da inteligência artificial.
                                    </p>

                                    {/* Features em Grid */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                                        <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-6">
                                            <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                                <Bot className="h-6 w-6" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-lg mb-2">Automação Inteligente</h3>
                                                <p className="text-white/90 text-sm">
                                                    Automatize tarefas recorrentes como agendamentos, alertas de medicação e geração de relatórios. A IA aprende com os padrões de uso e otimiza os processos automaticamente.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-6">
                                            <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                                <Brain className="h-6 w-6" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-lg mb-2">Sugestões Inteligentes</h3>
                                                <p className="text-white/90 text-sm">
                                                    Receba recomendações personalizadas para melhorar a gestão de cuidados, otimizar recursos e identificar oportunidades de melhoria contínua.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-6">
                                            <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                                <Clock4 className="h-6 w-6" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-lg mb-2">Previsão e Análise</h3>
                                                <p className="text-white/90 text-sm">
                                                    Analise padrões históricos para prever necessidades futuras, identificar tendências e antecipar demandas, permitindo planejamento mais eficiente.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-6">
                                            <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                                                <Zap className="h-6 w-6" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-lg mb-2">Eficiência Operacional</h3>
                                                <p className="text-white/90 text-sm">
                                                    Reduza o tempo gasto em tarefas administrativas em até 40% enquanto aumenta a precisão e a qualidade dos registros e processos.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* CTA */}
                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <Link to="/demonstracao">
                                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                <button className="bg-white text-purple-600 px-6 py-3 rounded-xl font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg flex items-center gap-2">
                                                    Experimentar IA Higeia
                                                    <ArrowRight className="h-5 w-5" />
                                                </button>
                                            </motion.div>
                                        </Link>
                                        <Link to="/contato">
                                            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                                <button className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white px-6 py-3 rounded-xl font-bold hover:bg-white/20 transition-all duration-300">
                                                    Saber Mais
                                                </button>
                                            </motion.div>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Seção: Recursos por Categoria */}
                <section className="py-16 sm:py-20 bg-white">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        {categoriasRecursos.map((categoria, categoriaIndex) => {
                            const IconCategoria = categoria.icon;
                            const colors = colorClasses[categoria.color];
                            
                            return (
                                <motion.div
                                    key={categoria.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{ duration: 0.6 }}
                                    className={`mb-20 ${categoriaIndex > 0 ? 'mt-20' : ''}`}
                                >
                                    {/* Cabeçalho da Categoria */}
                                    <div className="text-center mb-12">
                                        <div className={`inline-flex items-center justify-center w-16 h-16 ${colors.bg} rounded-2xl mb-6`}>
                                            <IconCategoria className={`h-8 w-8 ${colors.icon}`} />
                                        </div>
                                        <h2 className={`text-3xl sm:text-4xl font-black ${colors.title} mb-4`}>
                                            {categoria.title}
                                        </h2>
                                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                            {categoria.description}
                                        </p>
                                    </div>

                                    {/* Grid de Recursos */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                        {categoria.recursos.map((recurso, recursoIndex) => {
                                            const IconRecurso = recurso.icon;
                                            return (
                                                <motion.div
                                                    key={recursoIndex}
                                                    initial={{ opacity: 0, y: 20 }}
                                                    whileInView={{ opacity: 1, y: 0 }}
                                                    viewport={{ once: true }}
                                                    transition={{ delay: recursoIndex * 0.1, duration: 0.5 }}
                                                    whileHover={{ y: -8, transition: { duration: 0.2 } }}
                                                    className={`group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 ${colors.border} ${colors.bg}`}
                                                >
                                                    {/* Ícone */}
                                                    <div className={`inline-flex items-center justify-center w-14 h-14 ${colors.bg} rounded-xl mb-6 transition-transform duration-300 group-hover:scale-110`}>
                                                        <IconRecurso className={`h-7 w-7 ${colors.icon}`} />
                                                    </div>

                                                    {/* Título */}
                                                    <h3 className={`text-xl font-bold ${colors.title} mb-3`}>
                                                        {recurso.title}
                                                    </h3>

                                                    {/* Descrição */}
                                                    <p className="text-gray-600 leading-relaxed mb-6">
                                                        {recurso.description}
                                                    </p>

                                                    {/* Features */}
                                                    <ul className="space-y-2">
                                                        {recurso.features.map((feature, featureIndex) => (
                                                            <li key={featureIndex} className="flex items-start gap-2 text-sm text-gray-700">
                                                                <CheckCircle2 className={`h-5 w-5 ${colors.icon} flex-shrink-0 mt-0.5`} />
                                                                <span>{feature}</span>
                                                            </li>
                                                        ))}
                                                    </ul>

                                                    {recurso.cta && (
                                                        <Link
                                                            to={recurso.cta.href}
                                                            className={`relative z-10 mt-6 inline-flex items-center gap-2 text-sm font-semibold ${colors.title} hover:underline`}
                                                        >
                                                            {recurso.cta.label}
                                                            <ArrowRight className="h-4 w-4" />
                                                        </Link>
                                                    )}

                                                    {/* Efeito de brilho sutil no hover */}
                                                    <div className={`absolute inset-0 rounded-2xl transition-all duration-300 pointer-events-none ${colors.bg} opacity-0 group-hover:opacity-10`} />
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </section>

                {/* Seção: Benefícios Principais */}
                <section className="py-16 sm:py-20 bg-gradient-to-b from-gray-50 to-white">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="text-center mb-16"
                        >
                            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mb-4">
                                Por Que Escolher o{' '}
                                <span className="bg-gradient-to-r from-higeia-dark to-higeia-medium bg-clip-text text-transparent">
                                    Higeia.tech?
                                </span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                Mais do que um software, uma solução completa para transformar sua gestão de cuidados
                            </p>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {beneficios.map((beneficio, index) => {
                                const IconBeneficio = beneficio.icon;
                                const colors = colorClasses[beneficio.color];
                                
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: index * 0.1, duration: 0.5 }}
                                        className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center"
                                    >
                                        <div className={`inline-flex items-center justify-center w-16 h-16 ${colors.bg} rounded-2xl mb-6`}>
                                            <IconBeneficio className={`h-8 w-8 ${colors.icon}`} />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">
                                            {beneficio.title}
                                        </h3>
                                        <p className="text-gray-600 leading-relaxed">
                                            {beneficio.description}
                                        </p>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Seção: CTA Final */}
                <section className="py-16 sm:py-20 bg-gradient-to-r from-higeia-dark to-higeia-medium">
                    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                                <Heart className="h-10 w-10 text-white" />
                            </div>
                            <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
                                Pronto para Transformar sua Gestão de Cuidados?
                            </h2>
                            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                                Experimente o Higeia.tech gratuitamente e descubra como podemos otimizar sua operação e melhorar a qualidade dos cuidados prestados.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Link to="/demonstracao">
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <PrimaryButton className="!px-10 !py-4 !text-lg !font-bold !bg-white !text-higeia-dark hover:!bg-gray-100 !shadow-xl !border-0">
                                            Começar Agora
                                            <ArrowRight className="h-5 w-5 ml-2" />
                                        </PrimaryButton>
                                    </motion.div>
                                </Link>
                                <Link to="/contato">
                                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                        <PrimaryButton className="!px-10 !py-4 !text-lg !font-bold !bg-transparent !border-2 !border-white !text-white hover:!bg-white/10 !shadow-xl">
                                            Falar com Vendas
                                        </PrimaryButton>
                                    </motion.div>
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default RecursosPage;

